import { useEffect, useRef, useState } from 'react';

interface Project {
  id: number;
  title?: string;
  category: string;
  image: string;
  tags?: string[];
  brief?: string;
  isCaseStudy: boolean;
  link?: string;
}

export default function Projects() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const allProjects: Project[] = [
    {
      id: 1,
      title: 'BigBasket search flow redesign',
      category: 'Case Study',
      image: 'https://static.readdy.ai/image/2c242fed71dc9191960868e1d84810c9/4b44b21f080cd6519d3ca046dd251411.png',
      tags: ['UX Research', 'Search Flow', 'E Commerce'],
      brief: 'Redesigning the search experience for better product discovery',
      isCaseStudy: true,
      link: 'https://www.behance.net/gallery/217964681/Big-Basket-App-Redesign',
    },
    {
      id: 2,
      title: 'Trippie ui ux design & research',
      category: 'Case Study',
      image: 'https://static.readdy.ai/image/2c242fed71dc9191960868e1d84810c9/9a1c253837f15c2a6d7d17b5fe3b7c8e.png',
      tags: ['UI/UX Design', 'User Research', 'Mobile App'],
      brief: 'Complete UI/UX design and research for a travel planning application',
      isCaseStudy: true,
      link: 'https://www.behance.net/gallery/200181917/TRIPPIE-UIUX-Case-Study',
    },
    {
      id: 3,
      category: 'UI Design',
      image: 'https://static.readdy.ai/image/2c242fed71dc9191960868e1d84810c9/098058c0b406abba7388442d8a5148c3.jpeg',
      isCaseStudy: false,
      link: 'https://www.behance.net/charitpatel06',
    },
    {
      id: 4,
      category: 'UI Design',
      image: 'https://static.readdy.ai/image/2c242fed71dc9191960868e1d84810c9/7eaff29698314bb42f878779c88df7aa.png',
      isCaseStudy: false,
      link: 'https://www.behance.net/charitpatel06',
    },
    {
      id: 5,
      category: 'UI Design',
      image: 'https://static.readdy.ai/image/2c242fed71dc9191960868e1d84810c9/309680a765cd3e5bb41f848ff590e1b3.png',
      isCaseStudy: false,
      link: 'https://www.behance.net/charitpatel06',
    },
    {
      id: 6,
      category: 'UI Design',
      image: 'https://static.readdy.ai/image/2c242fed71dc9191960868e1d84810c9/cfc4c643562236b32ed37beb39cddd2a.png',
      isCaseStudy: false,
      link: 'https://www.behance.net/charitpatel06',
    },
    {
      id: 7,
      category: 'UI Design',
      image: 'https://static.readdy.ai/image/2c242fed71dc9191960868e1d84810c9/300c9520e109af39bf1b1cab1e3cd31e.png',
      isCaseStudy: false,
      link: 'https://www.behance.net/charitpatel06',
    },
  ];

  const handleProjectClick = (project: Project) => {
    if (project.link) {
      window.open(project.link, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSeeAllClick = () => {
    window.open('https://www.behance.net/charitpatel06', '_blank', 'noopener,noreferrer');
  };

  const caseStudies = allProjects.filter(p => p.isCaseStudy);
  const uiDesigns = allProjects.filter(p => !p.isCaseStudy);

  return (
    <section
      id="projects"
      ref={sectionRef}
      className="relative py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className={`text-center mb-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
          <span className="inline-block text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Portfolio</span>
          <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Featured Projects
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            A selection of my recent work in UI/UX design
          </p>
        </div>

        {/* Case Studies - Large Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {caseStudies.map((project, index) => (
            <div
              key={project.id}
              onClick={() => handleProjectClick(project)}
              className={`group bg-white border-2 border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
              }`}
              style={{ transitionDelay: `${index * 0.15}s` }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
            >
              {/* Project Image */}
              <div className="relative w-full h-96 overflow-hidden bg-slate-100">
                <img
                  src={project.image}
                  alt={project.title || project.category}
                  className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-500"
                />
                {/* Category Badge */}
                <div className="absolute top-6 right-6 rounded-lg px-5 py-2" style={{ backgroundColor: '#6b3d52' }}>
                  <span className="text-sm font-bold text-white">{project.category}</span>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-8 border-t-2 border-slate-200">
                <h3 className="text-3xl font-bold text-slate-900 mb-4 transition-colors duration-300"
                    style={{ color: '#1e293b' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#6b3d52'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#1e293b'}>
                  {project.title}
                </h3>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">{project.brief}</p>

                {/* Tags */}
                {project.tags && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-semibold"
                        style={{ backgroundColor: '#fdf2f8', color: '#7f1d1d' }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* View Case Study Link */}
                <div className="inline-flex items-center gap-2 font-semibold text-lg" style={{ color: '#6b3d52' }}>
                  View Case Study
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* UI Designs - Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {uiDesigns.map((project, index) => (
            <div
              key={project.id}
              onClick={() => handleProjectClick(project)}
              className={`group bg-white border-2 border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
              }`}
              style={{ transitionDelay: `${(index + 2) * 0.15}s` }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
            >
              {/* Project Image */}
              <div className="relative w-full h-80 overflow-hidden bg-slate-100">
                <img
                  src={project.image}
                  alt={project.category}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                {/* Category Badge */}
                <div className="absolute top-4 right-4 rounded-lg px-3 py-1" style={{ backgroundColor: '#6b3d52' }}>
                  <span className="text-xs font-bold text-white">{project.category}</span>
                </div>
              </div>

              {/* Simple Label */}
              <div className="p-5 border-t-2 border-slate-200">
                <div className="flex items-center gap-2 text-slate-700">
                  <i className="ri-image-line text-lg"></i>
                  <span className="text-sm font-medium">UI Design Showcase</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* See All Projects Button */}
        <div className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`} style={{ transitionDelay: '1s' }}>
          <button
            onClick={handleSeeAllClick}
            className="group px-10 py-5 text-white rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center gap-3 mx-auto whitespace-nowrap cursor-pointer"
            style={{ backgroundColor: '#6b3d52' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7d4a60'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b3d52'}
          >
            See All Projects
            <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform duration-300"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
